// components/oatt-upload/OattFileUpload.tsx
import { useState } from 'react';
import { Upload, Progress, Alert } from 'antd';
import { ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useOattUpload } from '@hooks/useOattUpload';
import type { UploadFileResult } from '@hooks/useOattUpload';
import styles from './OattFileUpload.module.css';
import { NSPButton } from '@components/ui/NSPButton';
import useSession from '@hooks/useSession';

interface OattFileUploadProps {
  onUploadComplete?: () => void;
}

function ValidationErrorPanel({ result }: { result: UploadFileResult }) {
  const errs = result.validationErrors ?? [];
  if (errs.length === 0) return null;

  const formatField = (field: string) =>
    field.replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());

  const message = errs
    .map(err => `• Row ${err.fileLineNumber ?? '?'} – AREF ${err.aref ?? 'Unknown'} – ${formatField(err.field)}: ${err.reason}`)
    .join('\n');

  return (
    <Alert
      type="error"
      showIcon
      closable
      className={styles.alert}
      message={
        <div>
          <b style={{ fontSize: '16px' }}>File upload failed on content validation</b>
          <details style={{ marginTop: '8px', cursor: 'pointer' }}>
            <summary style={{ fontSize: '15px' }}>View Details</summary>
            <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px' }}>{message}</pre>
          </details>
        </div>
      }
    />
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

const OattFileUpload = ({ onUploadComplete }: OattFileUploadProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { uploading, scanning, results, error, uploadFiles, reset } = useOattUpload();
  const { isOattBillApprover } = useSession();

  const handleSelectFiles = ({ fileList: newList }: any) => {
    setFileList(newList);
    if (results.length > 0 || error) reset();
  };

  const handleRemove = (file: UploadFile) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const handleUploadAll = async () => {
    const files = fileList.map((f) => f.originFileObj as File).filter(Boolean);
    if (files.length === 0) return;
    try {
      await uploadFiles(files);
      setFileList([]);
      onUploadComplete?.();
    } catch {
      // errors are shown via results / error state
    }
  };

  // Files still being scanned are excluded from the success/failure summary
  // below — they get their own "scanning" alert until the scan settles.
  const settledResults = results.filter((r) => !r.pending);
  const settledSuccess = settledResults.filter((r) => r.success);
  const settledFailed = settledResults.filter((r) => !r.success);

  const hasResults = settledResults.length > 0;
  const allSuccess = hasResults && settledFailed.length === 0;
  const partialSuccess = hasResults && settledSuccess.length > 0 && settledFailed.length > 0;
  const allFailed = hasResults && settledSuccess.length === 0;

  // Failed results that were blocked because Defender flagged them as malware
  const maliciousFailures = settledFailed.filter((r) => r.malicious);
  // Failed results that have structured per-row validation errors
  const validationFailures = settledFailed.filter((r) => r.validationErrors?.length);
  // Failed results that are plain server/network errors (no validation errors, not malicious)
  const plainFailures = settledFailed.filter((r) => !r.validationErrors?.length && !r.malicious);

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>Upload New File</div>
      {/* ── Per-file validation error panels (inline, no separate component) ── */}
      {validationFailures.map((r) => (
        <ValidationErrorPanel key={r.filename} result={r} />
      ))}
      {/* ── Network / auth error ── */}
      {error && (
        <Alert
          type="error"
          message="Upload Failed"
          description={error}
          showIcon
          closable
          onClose={reset}
          className={styles.alert}
        />
      )}

      {/* ── Files uploaded, virus scan in progress ── */}
      {scanning && (
        <Alert
          type="info"
          message="Scanning for Viruses"
          description="Your file(s) were uploaded and are being scanned before processing. This will update automatically."
          showIcon
          className={styles.alert}
        />
      )}

      {/* ── Malicious file(s) detected and removed ── */}
      {maliciousFailures.length > 0 && (
        <Alert
          type="error"
          message="Malicious File Detected"
          description={
            <span>
              The following file{maliciousFailures.length > 1 ? 's were' : ' was'} blocked and permanently removed: {maliciousFailures.map((r) => r.filename).join(', ')}.
            </span>
          }
          showIcon
          closable
          onClose={reset}
          className={styles.alert}
        />
      )}

      {/* ── All files succeeded ── */}
      {allSuccess && (
        <Alert
          type="success"
          message="Upload Successful"
          description={
            <span>
              {settledSuccess.length} file{settledSuccess.length > 1 ? 's' : ''} processed successfully.
            </span>
          }
          showIcon
          closable
          onClose={reset}
          className={styles.alert}
        />
      )}

      {/* ── Partial success (some files OK, some plain-failed) ── */}
      {partialSuccess && plainFailures.length > 0 && (
        <Alert
          type="warning"
          message="Partial Upload"
          description={
            <span>
              {settledSuccess.length} file{settledSuccess.length > 1 ? 's' : ''} succeeded,{' '}
              {plainFailures.length} failed:{' '}
              {plainFailures.map((r) => `${r.filename} — ${r.message}`).join('; ')}
            </span>
          }
          showIcon
          closable
          onClose={reset}
          className={styles.alert}
        />
      )}

      {/* ── All failed with plain error (no validation detail) ── */}
      {allFailed && !error && plainFailures.length > 0 && validationFailures.length === 0 && (
        <Alert
          type="error"
          message="Upload Failed"
          description={plainFailures.map((r) => `${r.filename}: ${r.message}`).join(' | ')}
          showIcon
          closable
          onClose={reset}
          className={styles.alert}
        />
      )}
      {/* ── File selector + list ── */}
      <Upload
        multiple
        accept=".xls,.xlsx,.csv"
        fileList={fileList}
        beforeUpload={() => false}
        onChange={handleSelectFiles}
        onRemove={handleRemove}
        showUploadList={{ showRemoveIcon: true, showPreviewIcon: false }}
      >
        <NSPButton icon={<UploadOutlined />} disabled={uploading || scanning || isOattBillApprover}>
          Select Files
        </NSPButton>
      </Upload>
      {/* ── Action bar: Upload All (left) | Refresh (right) ── */}
      <div className={styles.actionBar}>
        <NSPButton
          onClick={handleUploadAll}
          disabled={fileList.length === 0 || uploading || scanning || isOattBillApprover}
          loading={uploading || scanning}
        >
          {uploading ? 'Uploading...' : scanning ? 'Scanning...' : 'Upload All'}
        </NSPButton>

        <NSPButton
          icon={<ReloadOutlined />}
          onClick={() => globalThis.location.reload()}
        >
          Refresh
        </NSPButton>
      </div>



      {(uploading || scanning) && (
        <Progress percent={99} status="active" showInfo={false} className={styles.progress} />
      )}
    </div>
  );
};

export default OattFileUpload;